<script>
    import { onMount } from "svelte";

    import { isMobile, user } from "../../../stores";

    import {
        SearchInput,
        SearchSelect,
        SearchDateRangeInput,
    } from "../../../Components/Common/index.svelte";

    import { apiMethods } from "../../../Services/index.svelte";

    import {
        getDate,
        getUrlWithoutSearch,
        goToURL,
        showError,
    } from "../../../Utils/utilFunctions.svelte";

    import { dashboardFilterIntervals } from "../../../Utils/utilEnums.svelte";

    import DashboardContainer from "../components/DashboardContainer.svelte";
    import TotalsItem from "../components/TotalsItem.svelte";
    import EncountersItem from "../components/EncountersItem.svelte";
    import TotalEncounterStatus from "../components/TotalEncounterStatus.svelte";
    import TotalEncountersCreated from "../components/TotalEncountersCreated.svelte";
    import TotalPaymentsChart from "../components/TotalPaymentsChart.svelte";
    import MonthlyPaymentsItem from "../components/MonthlyPaymentsItem.svelte";

    import Calendar from "../../Calendar/Calendar.svelte";

    let filters = { dateFrom: null, dateTo: null, interval: 2 }; //default interval last 6 months

    let isGettingData = false,
        dashboardData = {};

    let totalEncountersCreated = {};

    let totalPatientBilled = {},
        totalHealthFundBilled = {};

    let totalPaymentsPatient = {},
        totalPaymentsHealthFund = {};

    let totalPatientPending = {},
        totalHealthFundPending = {};

    onMount(async () => {
        //this will load the data
        handleIntervalSelect();

        const params = window.location.href.split("?")[1];

        if (params) {
            const paramsArr = params.split("&");

            if (paramsArr.some((para) => para.includes("go="))) {
                const redirectURL = `/${paramsArr
                    .filter((para) => !para.includes("sso="))
                    .join("&")
                    .replace("go=", "")}`;

                goToURL(redirectURL);
            } else {
                getUrlWithoutSearch();
                goToURL("/");
            }
        }
    });

    const loadDashboardData = async (params) => {
        isGettingData = true;

        await apiMethods.getMethod
            .getDashboardDataForAdminAndManager(params)
            .then((res) => _handleDashboardData(res))
            .catch((err) => showError(err));

        isGettingData = false;
    };

    const handleIntervalSelect = () => {
        const today = new Date();
        const dateFrom = new Date();
        const dateTo = new Date();

        switch (filters.interval) {
            case 0:
                dateFrom.setMonth(today.getMonth() - 1);
                dateFrom.setDate(1);
                filters.dateFrom = getDate(dateFrom, "-", true);
                filters.dateTo = getDate(dateTo, "-", true);
                break;
            case 1:
                dateFrom.setMonth(today.getMonth() - 3);
                dateFrom.setDate(1);
                filters.dateFrom = getDate(dateFrom, "-", true);
                filters.dateTo = getDate(dateTo, "-", true);
                break;
            case 2:
                dateFrom.setMonth(today.getMonth() - 6);
                dateFrom.setDate(1);
                filters.dateFrom = getDate(dateFrom, "-", true);
                filters.dateTo = getDate(dateTo, "-", true);
                break;
            case 3:
                dateFrom.setFullYear(today.getFullYear() - 1);
                dateFrom.setMonth(0);
                dateFrom.setDate(1);
                filters.dateFrom = getDate(dateFrom, "-", true);
                filters.dateTo = getDate(dateTo, "-", true);
                break;
            case 5:
                dateFrom.setDate(1);
                filters.dateFrom = getDate(dateFrom, "-", true);
                filters.dateTo = getDate(dateTo, "-", true);
                break;
            case 6:
                dateFrom.setMonth(0);
                dateFrom.setDate(1);
                filters.dateFrom = getDate(dateFrom, "-", true);
                filters.dateTo = getDate(dateTo, "-", true);
                break;
            case 4:
            default:
                filters.dateFrom = getDate(dateFrom, "-", true);
                filters.dateTo = getDate(dateTo, "-", true);
                break;
        }

        if (filters.dateFrom && filters.dateTo)
            loadDashboardData(_getSearchParams());
    };

    const handleDateSelect = () => {
        if (filters.dateFrom && filters.dateTo)
            loadDashboardData(_getSearchParams());
    };

    // HELPER FUNCTIONS
    const _handleDashboardData = (res) => {
        if (!res || res.error)
            showError(
                res.error ??
                    "Failed to load dashboard data, please try again later or contact support...",
            );
        else {
            dashboardData = { ...res };

            if (res.encountersCreatedByDate)
                totalEncountersCreated = {
                    ...res.encountersCreatedByDate,
                };

            // Total Billed Data
            if (res.billedPatientsByDate)
                totalPatientBilled = {
                    ...res.billedPatientsByDate,
                };
            if (res.billedHealthFundByDate)
                totalHealthFundBilled = {
                    ...res.billedHealthFundByDate,
                };

            // Total Received Data
            if (res.paymentsReceivedPatientsByDate)
                totalPaymentsPatient = {
                    ...res.paymentsReceivedPatientsByDate,
                };
            if (res.paymentsReceivedHealthFundByDate)
                totalPaymentsHealthFund = {
                    ...res.paymentsReceivedHealthFundByDate,
                };

            // Total Pending Data
            if (res.pendingPaymentsPatientsByDate)
                totalPatientPending = {
                    ...res.pendingPaymentsPatientsByDate,
                };
            if (res.pendingPaymentsHealthFundByDate)
                totalHealthFundPending = {
                    ...res.pendingPaymentsHealthFundByDate,
                };
        }
    };

    const _getSearchParams = () => {
        let params = {};

        if (filters.dateFrom) params["filter[startdate]"] = filters.dateFrom;

        if (filters.dateTo) params["filter[enddate]"] = filters.dateTo;

        return params;
    };
</script>

<div class="flex flex-col justify-between items-center lg:flex-row">
    <div class="font-bold text-2xl p-5">
        Welcome {$user.name}
    </div>

    <div class="flex flex-col mr-2 mb-2 gap-5 lg:flex-row">
        <SearchSelect
            bind:value={filters.interval}
            title="Interval"
            onChangeFunc={handleIntervalSelect}
            isDisabled={isGettingData}
        >
            {#each dashboardFilterIntervals as interval}
                <option value={interval.id}>
                    {interval.type}
                </option>
            {/each}
        </SearchSelect>

        {#if !$user.settings?.disableDateRangeInput}
            <SearchDateRangeInput
                bind:startDate={filters.dateFrom}
                bind:endDate={filters.dateTo}
                title="Date Range"
                inputClass="borderB w-[272px]"
                on:change={handleDateSelect}
                showClearButton
                isDisable={isGettingData || filters.interval != 4}
            />
        {:else}
            <SearchInput
                bind:value={filters.dateFrom}
                title="Date from"
                type="date"
                isLong
                inputClass="borderB"
                inputFunc={handleDateSelect}
                isDisable={isGettingData || filters.interval != 4}
                maxDate={filters.dateTo ?? undefined}
            />

            <SearchInput
                bind:value={filters.dateTo}
                title="Date to"
                type="date"
                isLong
                inputClass="borderB"
                inputFunc={handleDateSelect}
                isDisable={isGettingData || filters.interval != 4}
                minDate={filters.dateFrom ?? undefined}
            />
        {/if}
    </div>
</div>

<div class="flex flex-1 flex-col">
    <div class="flex flex-1 flex-col mx-4 mb-4">
        <div class="relative flex-1">
            <div
                class="flex flex-col overflow-y-auto absolute inset-0 gap-4 mt-4"
            >
                <DashboardContainer
                    title={"Totals (Period / All Time)"}
                    isLoadingData={isGettingData}
                >
                    <TotalsItem totals={dashboardData.totals} />
                </DashboardContainer>

                <div
                    class="flex {$isMobile
                        ? 'flex-col'
                        : 'flex-col lg:flex-row'} flex-1 gap-4"
                >
                    <DashboardContainer
                        title={"Encounters"}
                        isLoadingData={isGettingData}
                    >
                        <EncountersItem
                            encounters={dashboardData.totalEncoutnersByInsurance}
                        />
                    </DashboardContainer>

                    <DashboardContainer
                        title={"Encounter Statuses"}
                        isLoadingData={isGettingData}
                    >
                        <TotalEncounterStatus
                            statuses={dashboardData.totalEncoutnersByStatus}
                        />
                    </DashboardContainer>
                </div>

                <DashboardContainer title={"Calendar"}>
                    <div class="flex p-4 w-full h-[1000px]">
                        <Calendar />
                    </div>
                </DashboardContainer>

                <div class="flex flex-row flex-1">
                    <DashboardContainer
                        title={"Total Encounters Created"}
                        isLoadingData={isGettingData}
                    >
                        <TotalEncountersCreated
                            {totalEncountersCreated}
                            dateFrom={filters.dateFrom}
                            dateTo={filters.dateTo}
                        />
                    </DashboardContainer>
                </div>

                <div class="flex flex-row flex-1">
                    <DashboardContainer
                        title={"Billed"}
                        isLoadingData={isGettingData}
                    >
                        <TotalPaymentsChart
                            totalPaymentsPatient={totalPatientBilled}
                            totalPaymentsHealthFund={totalHealthFundBilled}
                            dateFrom={filters.dateFrom}
                            dateTo={filters.dateTo}
                            type="billed"
                        />
                    </DashboardContainer>
                </div>

                <div class="flex flex-row flex-1">
                    <DashboardContainer
                        title={"Payments Received"}
                        isLoadingData={isGettingData}
                    >
                        <TotalPaymentsChart
                            {totalPaymentsPatient}
                            {totalPaymentsHealthFund}
                            dateFrom={filters.dateFrom}
                            dateTo={filters.dateTo}
                            type="received"
                        />
                    </DashboardContainer>
                </div>

                <div class="flex flex-row flex-1">
                    <DashboardContainer
                        title={"Payments Pending"}
                        isLoadingData={isGettingData}
                    >
                        <TotalPaymentsChart
                            totalPaymentsPatient={totalPatientPending}
                            totalPaymentsHealthFund={totalHealthFundPending}
                            dateFrom={filters.dateFrom}
                            dateTo={filters.dateTo}
                            type="pending"
                        />
                    </DashboardContainer>
                </div>

                <div class="flex flex-row flex-1">
                    <DashboardContainer
                        title={"Monthly Payments"}
                        isLoadingData={isGettingData}
                    >
                        <MonthlyPaymentsItem
                            monthlyPayments={dashboardData.monthlyPayments}
                        />
                    </DashboardContainer>
                </div>
            </div>
        </div>
    </div>
</div>
